import React, { useState, useCallback, useRef } from 'react';

/**
 * Hook for managing message selection
 */
const UseMessageSelection = () => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const lastSelectedIndexRef = useRef(null);

  const toggleSelection = useCallback((id, index, isShiftClick, allMessages) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      
      // Handle shift-click range selection
      if (isShiftClick && lastSelectedIndexRef.current !== null && allMessages) {
        const start = Math.min(lastSelectedIndexRef.current, index);
        const end = Math.max(lastSelectedIndexRef.current, index);
        
        // Select all messages in the range
        for (let i = start; i <= end; i++) {
          if (allMessages[i]) {
            next.add(allMessages[i].id);
          }
        }
      } else {
        // Normal toggle
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
      }
      
      return next;
    });
    
    // Update last selected index only on non-shift clicks or first shift-click
    if (!isShiftClick || lastSelectedIndexRef.current === null) {
      lastSelectedIndexRef.current = index;
    }
  }, []);

  const selectAll = useCallback((messages) => {
    setSelectedIds(new Set(messages.map(m => m.id)));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastSelectedIndexRef.current = null;
  }, []);

  const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);

  return { selectedIds, toggleSelection, selectAll, clearSelection, isSelected };
};

export default UseMessageSelection;